// =============================================================================
// BUDDY ABUSE ANALYTICS CARDS — Platform-side moderation visibility
//
// Two surfaces, both backed by the same admin-buddy-analytics edge
// function:
//
//   • BuddyAbuseAnalyticsCard  (per-tenant)
//       Shows on the Tenant Dashboard inside the Platform app. KPI grid
//       (strikers / blocked / strikes 24h-7d-30d) + top abuse terms +
//       recent strike feed. Hidden entirely when the tenant has zero
//       abuse activity, to avoid "0 of 0" noise.
//
//   • BuddyAbuseSuperAdminCard (cross-tenant)
//       Shows on the Platform Overview. Aggregate KPIs across every
//       tenant, followed by a per-tenant table with strikers / blocked
//       / 7-day strike count. Sorted: blocked-leading first, then 7-day
//       activity. Hidden entirely when no tenant has any strikes.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Ban, AlertTriangle, ExternalLink, RotateCcw, RefreshCw, Bell, ShieldCheck } from 'lucide-react';
import { usePlatformStore } from '@/lib/store';

// ─── Edge-function helpers (Platform → App DB) ─────────────────────
async function callBuddyAnalytics(body: any) {
  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/admin-buddy-analytics`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify(body),
  });
  return res.json();
}

interface BlockedUserRow {
  user_id: string;
  email: string | null;
  full_name: string | null;
  strike_count: number;
  blocked: boolean;
  blocked_at: string | null;
  last_strike_at: string | null;
  last_strike_terms: string[] | null;
  last_strike_excerpt: string | null;
}

interface UnblockRequest {
  id: string;
  user_id: string;
  email: string | null;
  full_name: string | null;
  message: string | null;
  requested_at: string;
}

interface SuperAdminUnblockRequest extends UnblockRequest {
  tenant_id: string;
}

const APP_SUPABASE_URL = import.meta.env.VITE_APP_SUPABASE_URL || '';
const PROVISION_SECRET = import.meta.env.VITE_PROVISION_SECRET || '';

interface RecentStrike {
  created_at: string;
  matched_terms: string[];
  excerpt: string;
  group_id: string | null;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
}

interface TopTerm {
  term: string;
  count: number;
}

interface TenantAbuseData {
  total_strikers:           number;
  total_blocked:            number;
  total_unblocked_users:    number;
  total_strikes:            number;
  strikes_24h:              number;
  strikes_7d:               number;
  strikes_30d:              number;
  top_terms:                TopTerm[];
  recent_strikes:           RecentStrike[];
  // Configured gating (from tenant_plan_features)
  strike_limit?:            number;
  max_chats_limit?:         number | null;
  active_chat_memberships?: number;
}

interface SuperAdminAbuseRow {
  tenant_id:             string;
  total_strikers:        number;
  total_blocked:         number;
  total_unblocked_users: number;
  total_strikes:         number;
  strikes_24h:           number;
  strikes_7d:            number;
  strikes_30d:           number;
  last_strike_at:        string | null;
}

// ── Per-tenant card ─────────────────────────────────────────────────
//
// Slim overview-only variant. Shows KPI band + a "Manage in Users"
// link that drills into the Users tab where per-user moderation
// detail (recent strikes, top terms, unblock action) lives. The full
// blocked-users list and the recent-strikes feed used to live here
// but were moved to Users so admins manage moderation alongside the
// rest of the user roster.
export function BuddyAbuseAnalyticsCard({
  tenantId,
  manageHref,
}: {
  tenantId: string;
  /** Optional path to the Users tab so the card can link out. */
  manageHref?: string;
}) {
  const [data, setData] = useState<TenantAbuseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  const load = useCallback(async () => {
    if (!APP_SUPABASE_URL || !PROVISION_SECRET) { setLoading(false); return; }
    setLoading(true);
    try {
      const [summary, reqs] = await Promise.all([
        callBuddyAnalytics({ mode: 'abuse_tenant', tenant_id: tenantId }),
        callBuddyAnalytics({ mode: 'list_unblock_requests', tenant_id: tenantId }),
      ]);
      if (summary.ok) setData(summary.data);
      if (reqs.ok) setPendingRequestCount((reqs.data ?? []).length);
    } catch (err) {
      console.warn('[BuddyAbuseAnalyticsCard] failed:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return null;
  // Hide entirely when there's no abuse activity yet AND no pending
  // requests are sitting in queue.
  if (
    data.total_strikes === 0 &&
    data.total_strikers === 0 &&
    data.total_blocked === 0 &&
    pendingRequestCount === 0
  ) return null;

  const headlineTone = data.total_blocked > 0
    ? 'amber'
    : data.strikes_7d > 0
      ? 'amber-light'
      : 'neutral';

  return (
    <div className="rounded-xl border border-surface-200 bg-surface-0 p-5">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <ShieldAlert size={16} className="text-amber-600" />
        <h3 className="text-sm font-semibold text-surface-900">Buddy Trips moderation</h3>
        {pendingRequestCount > 0 && (
          <span
            className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/50 rounded-md px-2 py-0.5 animate-pulse"
            title={`${pendingRequestCount} unblock request${pendingRequestCount === 1 ? '' : 's'} pending`}
          >
            <Bell size={11} />
            {pendingRequestCount} request{pendingRequestCount === 1 ? '' : 's'}
          </span>
        )}
        {pendingRequestCount === 0 && data.total_blocked > 0 && (
          <span className="ml-auto text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-700/50 rounded-md px-2 py-0.5">
            {data.total_blocked} blocked
          </span>
        )}
        {pendingRequestCount === 0 && data.total_blocked === 0 && data.strikes_7d > 0 && (
          <span className="ml-auto text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/50 rounded-md px-2 py-0.5">
            {data.strikes_7d} strikes this week
          </span>
        )}
      </div>

      {/* KPI grid — five tiles. Unblocked = lifetime count of distinct
          users who've been unblocked at least once in this tenant. */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat icon={<AlertTriangle size={14} />} label="Active strikers" value={data.total_strikers}        tone="amber" />
        <Stat icon={<Ban size={14} />}           label="Blocked"         value={data.total_blocked}         tone={data.total_blocked > 0 ? 'red' : 'neutral'} />
        <Stat icon={<ShieldCheck size={14} />}   label="Unblocked users" value={data.total_unblocked_users} tone={data.total_unblocked_users > 0 ? 'emerald' : 'neutral'} />
        <Stat icon={<ShieldAlert size={14} />}   label="Strikes · 24h"   value={data.strikes_24h} />
        <Stat icon={<ShieldAlert size={14} />}   label="Strikes · 7d"    value={data.strikes_7d} />
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        {data.total_strikes > 0 ? (
          <p className="text-xs text-surface-500">
            {data.total_strikes.toLocaleString()} total strike{data.total_strikes === 1 ? '' : 's'} ·{' '}
            {data.strikes_30d.toLocaleString()} in the last 30 days
          </p>
        ) : <span />}
        {manageHref && (
          <Link
            to={manageHref}
            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 dark:text-brand-400 hover:underline"
          >
            Manage in Users <ExternalLink size={11} />
          </Link>
        )}
      </div>
    </div>
  );
}

// ── Cross-tenant super-admin card ──────────────────────────────────
export function BuddyAbuseSuperAdminCard() {
  const [rows, setRows] = useState<SuperAdminAbuseRow[] | null>(null);
  const [requests, setRequests] = useState<SuperAdminUnblockRequest[]>([]);
  const [loading, setLoading] = useState(true);

  // Tenant name + slug come from the Platform store. The App DB doesn't
  // know human names — it only knows tenant_id — so we resolve here.
  const tenants = usePlatformStore(s => s.tenants);
  const tenantNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenants) {
      m.set(t.id, t.name || t.slug || '(unknown)');
    }
    return m;
  }, [tenants]);

  // Bucket pending requests by tenant so we can surface a count badge
  // on each tenant row in the table.
  const requestsByTenant = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of requests) {
      m.set(r.tenant_id, (m.get(r.tenant_id) ?? 0) + 1);
    }
    return m;
  }, [requests]);

  useEffect(() => {
    if (!APP_SUPABASE_URL || !PROVISION_SECRET) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const [abuse, reqs] = await Promise.all([
          callBuddyAnalytics({ mode: 'abuse_super_admin' }),
          callBuddyAnalytics({ mode: 'list_all_unblock_requests' }),
        ]);
        if (!cancelled) {
          if (abuse.ok) setRows(abuse.data ?? []);
          if (reqs.ok) setRequests(reqs.data ?? []);
        }
      } catch (err) {
        console.warn('[BuddyAbuseSuperAdminCard] failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !rows) return null;
  // Stay rendered when there are pending requests even if no abuse rows
  // remain — operator still needs to see + act on the request queue.
  if (rows.length === 0 && requests.length === 0) return null;

  const totals = rows.reduce(
    (acc, r) => ({
      strikers:           acc.strikers           + r.total_strikers,
      blocked:            acc.blocked            + r.total_blocked,
      unblocked_users:    acc.unblocked_users    + (r.total_unblocked_users ?? 0),
      strikes_24h:        acc.strikes_24h        + r.strikes_24h,
      strikes_7d:         acc.strikes_7d         + r.strikes_7d,
      strikes_30d:        acc.strikes_30d        + r.strikes_30d,
    }),
    { strikers: 0, blocked: 0, unblocked_users: 0, strikes_24h: 0, strikes_7d: 0, strikes_30d: 0 },
  );

  return (
    <div className="rounded-xl border border-surface-200 bg-surface-0 p-5">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <ShieldAlert size={18} className="text-amber-600" />
        <h2 className="text-base font-semibold text-surface-900">Buddy Trips abuse — across tenants</h2>
        {requests.length > 0 && (
          <span
            className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/50 rounded-md px-2 py-0.5 animate-pulse"
            title={`${requests.length} unblock request${requests.length === 1 ? '' : 's'} pending across all tenants`}
          >
            <Bell size={11} />
            {requests.length} unblock request{requests.length === 1 ? '' : 's'} pending
          </span>
        )}
        {requests.length === 0 && totals.blocked > 0 && (
          <span className="ml-auto text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-700/50 rounded-md px-2 py-0.5">
            {totals.blocked} blocked across {rows.filter(r => r.total_blocked > 0).length} tenant{rows.filter(r => r.total_blocked > 0).length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <p className="text-xs text-surface-500 mb-4">
        Auto-detected abuse strikes per tenant. 10 strikes auto-blocks the user from that tenant's chats; tenant admins can unblock.
      </p>

      {/* Totals — six tiles. Includes Unblocked Users so the super-
          admin can pulse-check moderation activity across tenants. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Stat icon={<AlertTriangle size={14} />} label="Active strikers" value={totals.strikers}        tone="amber" />
        <Stat icon={<Ban size={14} />}           label="Blocked"         value={totals.blocked}         tone={totals.blocked > 0 ? 'red' : 'neutral'} />
        <Stat icon={<ShieldCheck size={14} />}   label="Unblocked users" value={totals.unblocked_users} tone={totals.unblocked_users > 0 ? 'emerald' : 'neutral'} />
        <Stat icon={<ShieldAlert size={14} />}   label="Strikes · 24h"   value={totals.strikes_24h} />
        <Stat icon={<ShieldAlert size={14} />}   label="Strikes · 7d"    value={totals.strikes_7d} />
        <Stat icon={<ShieldAlert size={14} />}   label="Strikes · 30d"   value={totals.strikes_30d} />
      </div>

      {/* Per-tenant table */}
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-left text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-surface-500">
            <tr className="border-b border-surface-200">
              <th className="py-2 px-2 font-semibold">Tenant</th>
              <th className="py-2 px-2 font-semibold text-right">Strikers</th>
              <th className="py-2 px-2 font-semibold text-right">Blocked</th>
              <th className="py-2 px-2 font-semibold text-right">Unblocked</th>
              <th className="py-2 px-2 font-semibold text-right">24h</th>
              <th className="py-2 px-2 font-semibold text-right">7d</th>
              <th className="py-2 px-2 font-semibold text-right">30d</th>
              <th className="py-2 px-2 font-semibold">Last strike</th>
              <th className="py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr
                key={r.tenant_id}
                className={`border-b border-surface-100 hover:bg-surface-50/50 transition-colors ${
                  r.total_blocked > 0 ? 'bg-red-50/30 dark:bg-red-950/10' : ''
                }`}
              >
                <td className="py-2.5 px-2 font-medium text-surface-900 truncate max-w-[180px]">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate">
                      {tenantNames.get(r.tenant_id) || r.tenant_id.slice(0, 8) + '…'}
                    </span>
                    {requestsByTenant.get(r.tenant_id) && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 shrink-0"
                        title={`${requestsByTenant.get(r.tenant_id)} unblock request${requestsByTenant.get(r.tenant_id) === 1 ? '' : 's'} pending`}
                      >
                        <Bell size={9} />
                        {requestsByTenant.get(r.tenant_id)}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-2.5 px-2 text-right text-surface-700">
                  {r.total_strikers || '—'}
                </td>
                <td className="py-2.5 px-2 text-right">
                  {r.total_blocked > 0 ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-bold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                      {r.total_blocked}
                    </span>
                  ) : (
                    <span className="text-surface-400">—</span>
                  )}
                </td>
                <td className="py-2.5 px-2 text-right">
                  {(r.total_unblocked_users ?? 0) > 0 ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-bold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                      {r.total_unblocked_users}
                    </span>
                  ) : (
                    <span className="text-surface-400">—</span>
                  )}
                </td>
                <td className="py-2.5 px-2 text-right text-surface-700 tabular-nums">{r.strikes_24h || '—'}</td>
                <td className="py-2.5 px-2 text-right text-surface-700 tabular-nums">{r.strikes_7d || '—'}</td>
                <td className="py-2.5 px-2 text-right text-surface-700 tabular-nums">{r.strikes_30d || '—'}</td>
                <td className="py-2.5 px-2 text-surface-500 text-xs">
                  {r.last_strike_at ? formatRelativeTime(r.last_strike_at) : '—'}
                </td>
                <td className="py-2.5 px-2 text-right">
                  <Link
                    to={`/tenants/${r.tenant_id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 dark:text-brand-400 hover:underline"
                    title="Open tenant dashboard"
                  >
                    View <ExternalLink size={11} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────
function Stat({
  icon, label, value, tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone?: 'red' | 'amber' | 'emerald' | 'neutral';
}) {
  const toneClasses =
    tone === 'red'
      ? 'border-red-200/80 bg-red-50/40 dark:bg-red-900/10 dark:border-red-800/40'
      : tone === 'amber'
        ? 'border-amber-200/80 bg-amber-50/40 dark:bg-amber-900/10 dark:border-amber-800/40'
        : tone === 'emerald'
          ? 'border-emerald-200/80 bg-emerald-50/40 dark:bg-emerald-900/10 dark:border-emerald-800/40'
          : 'border-surface-200 bg-surface-50/60 dark:border-surface-200 dark:bg-surface-0';
  return (
    <div className={`p-3 rounded-lg border ${toneClasses}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-surface-500 mb-1">
        {icon} {label}
      </div>
      <div className="text-xl font-bold text-surface-900 tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}

function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
